/**
 * Newsletter modal dialog with focus trapping and iframe content loading.
 */
const newsletter = {
  state: {
    isInitialized: false,
    modalElement: null,
    previouslyFocusedElement: null,
  },

  /**
   * Initializes the newsletter component.
   * @returns {void}
   */
  init() {
    if (this.state.isInitialized) return;

    this.state.modalElement = document.querySelector('.newsletter-dialog');
    const newsletterOpen = document.querySelector('[data-toggle-offcanvas="newsletter-open"]');

    if (!this.state.modalElement) {
      console.warn('newsletter: Modal element not found');
      return;
    }

    if (!newsletterOpen) {
      console.warn('newsletter: Newsletter open button not found');
      return;
    }

    this.setupEventListeners(newsletterOpen);
    this.state.isInitialized = true;
  },

  /**
   * @param {HTMLElement} newsletterOpen - Button element that opens the newsletter modal.
   * @private
   */
  setupEventListeners(newsletterOpen) {
    const dataUrl = newsletterOpen.dataset.url;

    newsletterOpen.addEventListener('click', (e) => {
      e.preventDefault();
      this.openModal(dataUrl);
    });

    newsletterOpen.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openModal(dataUrl);
      }
    });

    const closeButton = this.state.modalElement.querySelector('.modal__close-button');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });

      closeButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.closeModal();
        }
      });
    }

    this._boundKeydown = (e) => {
      if (!this.state.modalElement?.open) return;
      if (e.key === 'Escape') {
        this.closeModal();
        return;
      }
      if (e.key === 'Tab') {
        this.handleTabKey(e);
      }
    };
    document.addEventListener('keydown', this._boundKeydown);
  },

  /**
   * @param {KeyboardEvent} e - Keyboard event.
   * @private
   */
  handleTabKey(e) {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  },

  /**
   * @returns {HTMLElement[]}
   * @private
   */
  getFocusableElements() {
    if (!this.state.modalElement) return [];

    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      'iframe',
    ].join(', ');

    return Array.from(this.state.modalElement.querySelectorAll(selector));
  },

  /**
   * Opens the modal and loads the iframe content.
   * @param {string} dataUrl - URL to load in the newsletter iframe.
   * @returns {void}
   */
  openModal(dataUrl) {
    if (!this.state.isInitialized) {
      this.init();
    }

    if (!this.state.modalElement) {
      console.error('newsletter: Cannot open modal - modal element not found');
      return;
    }

    this.state.previouslyFocusedElement = document.activeElement;

    const newsletterIframe = this.state.modalElement.querySelector('iframe');
    if (newsletterIframe && dataUrl) {
      newsletterIframe.src = dataUrl;
    }

    this.state.modalElement.classList.add('slide-in-from-top');
    this.state.modalElement.showModal();

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      this.state.modalElement.focus();
    }

    document.body.style.overflow = 'hidden';
  },

  /**
   * Closes the modal after the exit animation completes.
   * @returns {void}
   */
  closeModal() {
    if (!this.state.modalElement) {
      console.warn('newsletter: Cannot close modal - modal element not found');
      return;
    }

    this.state.modalElement.classList.add('slide-out-to-top');

    setTimeout(() => {
      this.state.modalElement.close();
      this.state.modalElement.classList.remove('slide-in-from-top', 'slide-out-to-top');

      const newsletterIframe = this.state.modalElement.querySelector('iframe');
      if (newsletterIframe) {
        newsletterIframe.src = '';
      }
    }, 300);

    document.body.style.overflow = '';

    if (this.state.previouslyFocusedElement) {
      this.state.previouslyFocusedElement.focus();
    }
  },

  /**
   * Removes event listeners and resets state.
   * @returns {void}
   */
  destroy() {
    if (this._boundKeydown) {
      document.removeEventListener('keydown', this._boundKeydown);
      this._boundKeydown = null;
    }
    this.state.isInitialized = false;
    this.state.modalElement = null;
    this.state.previouslyFocusedElement = null;
  },
};

export default newsletter;
